/**
 * High Contrast Mode Utilities
 *
 * Provides utilities for detecting and supporting high contrast mode
 * across different operating systems and browsers.
 */

/**
 * Detect if high contrast mode is active
 * Works on Windows High Contrast Mode and other OS-level settings
 */
export const isHighContrastMode = (): boolean => {
  // Method 1: Check for forced-colors media query (modern browsers)
  if (window.matchMedia) {
    const forcedColors = window.matchMedia('(forced-colors: active)')
    if (forcedColors.matches) {
      return true
    }

    // Method 2: Check for -ms-high-contrast (legacy IE/Edge)
    const msHighContrast = window.matchMedia('(-ms-high-contrast: active)')
    if (msHighContrast.matches) {
      return true
    }

    // Method 3: Check for prefers-contrast (newer standard)
    const prefersContrast = window.matchMedia('(prefers-contrast: more)')
    if (prefersContrast.matches) {
      return true
    }
  }

  return false
}

/**
 * Get the high contrast mode theme (black-on-white or white-on-black)
 */
export const getHighContrastTheme = (): 'light' | 'dark' | 'none' => {
  if (!isHighContrastMode()) {
    return 'none'
  }

  // Check for black-on-white (light theme)
  if (window.matchMedia && window.matchMedia('(-ms-high-contrast: black-on-white)').matches) {
    return 'light'
  }

  // Check for white-on-black (dark theme)
  if (window.matchMedia && window.matchMedia('(-ms-high-contrast: white-on-black)').matches) {
    return 'dark'
  }

  // Default to light if we can't determine
  return 'light'
}

/**
 * Apply high contrast mode adjustments to an element
 */
export const applyHighContrastAdjustments = (element: HTMLElement): void => {
  if (!isHighContrastMode()) {
    return
  }

  // Add high contrast class for CSS targeting
  element.classList.add('high-contrast-mode')

  // Ensure borders are visible
  const computedStyle = window.getComputedStyle(element)
  if (computedStyle.borderWidth === '0px' || computedStyle.borderStyle === 'none') {
    element.style.border = '1px solid currentColor'
  }

  // Ensure focus indicators are visible
  element.style.outlineWidth = '2px'
  element.style.outlineStyle = 'solid'
}

/**
 * Remove high contrast mode adjustments from an element
 */
export const removeHighContrastAdjustments = (element: HTMLElement): void => {
  element.classList.remove('high-contrast-mode')
  element.style.border = ''
  element.style.outlineWidth = ''
  element.style.outlineStyle = ''
}

/**
 * Hook for detecting high contrast mode changes
 */
export const useHighContrastMode = (callback: (isActive: boolean) => void) => {
  React.useEffect(() => {
    if (!window.matchMedia) {
      return
    }

    const queries = [
      window.matchMedia('(forced-colors: active)'),
      window.matchMedia('(-ms-high-contrast: active)'),
      window.matchMedia('(prefers-contrast: more)')
    ]

    const handleChange = () => {
      callback(isHighContrastMode())
    }

    // Add listeners to all queries
    queries.forEach(query => {
      if (query.addEventListener) {
        query.addEventListener('change', handleChange)
      } else if (query.addListener) {
        // Fallback for older browsers
        query.addListener(handleChange)
      }
    })

    // Initial check
    handleChange()

    return () => {
      queries.forEach(query => {
        if (query.removeEventListener) {
          query.removeEventListener('change', handleChange)
        } else if (query.removeListener) {
          query.removeListener(handleChange)
        }
      })
    }
  }, [callback])
}

/**
 * Get high contrast safe color for text
 */
export const getHighContrastTextColor = (backgroundColor: string): string => {
  if (!isHighContrastMode()) {
    return 'inherit'
  }

  // In high contrast mode, use system colors
  const theme = getHighContrastTheme()
  return theme === 'dark' ? 'white' : 'black'
}

/**
 * Get high contrast safe color for borders
 */
export const getHighContrastBorderColor = (): string => {
  if (!isHighContrastMode()) {
    return 'inherit'
  }

  // Use currentColor in high contrast mode for maximum compatibility
  return 'currentColor'
}

/**
 * Check if an element has sufficient contrast in high contrast mode
 */
export const hasHighContrastSupport = (element: HTMLElement): boolean => {
  if (!isHighContrastMode()) {
    return true
  }

  const computedStyle = window.getComputedStyle(element)

  // Check if element has visible borders or outlines
  const hasBorder = computedStyle.borderWidth !== '0px' && computedStyle.borderStyle !== 'none'
  const hasOutline = computedStyle.outlineWidth !== '0px' && computedStyle.outlineStyle !== 'none'

  // Check if element has background color
  const hasBackground = computedStyle.backgroundColor !== 'rgba(0, 0, 0, 0)' &&
                        computedStyle.backgroundColor !== 'transparent'

  return hasBorder || hasOutline || hasBackground
}

/**
 * Initialize high contrast mode support for the application
 */
export const initHighContrastMode = (): (() => void) => {
  const updateHighContrastClass = () => {
    if (isHighContrastMode()) {
      document.documentElement.classList.add('high-contrast-mode')
      document.documentElement.setAttribute('data-high-contrast-theme', getHighContrastTheme())
    } else {
      document.documentElement.classList.remove('high-contrast-mode')
      document.documentElement.removeAttribute('data-high-contrast-theme')
    }
  }

  // Initial update
  updateHighContrastClass()

  // Listen for changes
  if (window.matchMedia) {
    const queries = [
      window.matchMedia('(forced-colors: active)'),
      window.matchMedia('(-ms-high-contrast: active)'),
      window.matchMedia('(prefers-contrast: more)')
    ]

    queries.forEach(query => {
      if (query.addEventListener) {
        query.addEventListener('change', updateHighContrastClass)
      } else if (query.addListener) {
        query.addListener(updateHighContrastClass)
      }
    })

    return () => {
      queries.forEach(query => {
        if (query.removeEventListener) {
          query.removeEventListener('change', updateHighContrastClass)
        } else if (query.removeListener) {
          query.removeListener(updateHighContrastClass)
        }
      })
    }
  }

  return () => {}
}

// Re-export React for hooks
import React from 'react'
